/**
 * ehr#614 (Chase 10-23): pressing the left and right mouse buttons together on the Touches menu
 * logged the touch twice. Actions that log or count something act only on a plain primary click
 * (a left click, a tap, or Enter / Space on the button), and only one save runs at a time.
 */
export interface ClickLike {
  /** the button that made this click: 0 = primary (left), 1 = middle, 2 = right */
  button: number;
  /** the buttons still held down after it, as a bit mask (1 = left, 2 = right, 4 = middle) */
  buttons?: number;
  ctrlKey?: boolean;
}

/** True only for a plain primary click: not the right or middle button, not a Mac Control-click
 * (which opens the context menu), and not a left click while another button is still held. */
export function isPrimaryClick(e: ClickLike): boolean {
  if (e.button !== 0) return false;
  if (e.ctrlKey) return false;
  if (typeof e.buttons === 'number' && (e.buttons & ~1) !== 0) return false;
  return true;
}

/** The part of a DOM element the drawer's right-click check needs (a fake in the tests). */
export interface ClosestLike {
  closest(selector: string): unknown;
}

/** Where the browser's own right-click menu stays (paste in inputs, open a link, media). */
const NATIVE_MENU =
  'input, textarea, select, a, img, video, audio, [contenteditable="true"], .dd, .dd-panel';

/** A window opened over the drawer: a form being filled, a note, a confirm. */
const NESTED_WINDOW = '[role="dialog"], [role="alertdialog"], .modal-backdrop';

/**
 * ehr#614 (Chase 10-24): a right-click on the drawer opens its Touches menu, except where the
 * browser's own menu is needed, and never inside a window opened over the drawer (filling a form
 * from the Forms tab). That window sits inside the drawer, so its right-clicks reached the drawer
 * and opened the Touches menu over the form. `drawer` is the drawer itself (its own
 * role="dialog" doesn't count).
 */
export function drawerMenuAllowed(target: ClosestLike, drawer: unknown): boolean {
  if (target.closest(NATIVE_MENU)) return false;
  const win = target.closest(NESTED_WINDOW);
  if (win && win !== drawer) return false;
  return true;
}

const inFlight = new Set<string>();

/** Whether a save under this key is still running. */
export function isInFlight(key: string): boolean {
  return inFlight.has(key);
}

/**
 * Runs `fn` unless a save under the same key is still running, in which case it does nothing and
 * returns null. The key is module-wide, so two menus open for the same contact (the + Touches one
 * and the right-click one) still log once.
 */
export function runOnce<T>(key: string, fn: () => Promise<T>): Promise<T> | null {
  if (inFlight.has(key)) return null;
  inFlight.add(key);
  let p: Promise<T>;
  try {
    p = fn();
  } catch (e) {
    inFlight.delete(key);
    return Promise.reject(e instanceof Error ? e : new Error(String(e)));
  }
  return p.finally(() => {
    inFlight.delete(key);
  });
}
